import { api, unwrap } from "@lumberset/api-client";
import { shortId, TicketStatusBadge } from "@lumberset/support-ui";
import {
  ago,
  date,
  dateTime,
  initials,
  money,
  ReviewMode,
  ReviewStatus,
} from "@lumberset/takeoff-ui";
import {
  Avatar,
  Button,
  Container,
  Drawer,
  DropdownMenu,
  EmptyState,
  Field,
  Input,
  Prompt,
  SectionHeader,
  SectionRow,
  Select,
  StatusBadge,
  Table,
  Tabs,
  Textarea,
  type Tone,
  TwoColumn,
  toast,
} from "@lumberset/ui";
import {
  ArrowUpRightOnBox,
  ArrowUturnLeft,
  BookOpen,
  CheckCircle,
  EllipsisHorizontal,
  Envelope,
  Key,
  Lifebuoy,
  ListCheckbox,
  MinusCircle,
  PencilSquare,
  ScrollText,
  User,
  XCircle,
} from "@medusajs/icons";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { auditSentence, auditTargetLabel } from "../../../components/audit";
import { Crumbs } from "../../../components/crumbs";
import { SignInAsPrompt } from "../../../components/sign-in-as";
import { type TeamMember, teamEmailActions, teamMemberQuery } from "../../../lib/queries-team";
import { MenuText } from "../yards/-parts";
import { ConfirmEmailPrompt, EmailNotConfirmed } from "./-email";
import {
  CountBars,
  collectionLabel,
  isStaffRole,
  minutes,
  NumbersGrid,
  RoleBadge,
  reasonLabel,
  roleChoices,
  type StaffRoleValue,
  StaffStatus,
} from "./-parts";

export const Route = createFileRoute("/_app/staff/$id")({
  loader: ({ context: { queryClient }, params }) =>
    queryClient.ensureQueryData(teamMemberQuery(params.id)),
  component: StaffMemberScreen,
});

type Person = TeamMember["person"];
type Period = "week" | "month" | "quarter";
type Confirm = "deactivate" | "reactivate" | "remove" | "email" | "sign-in-as" | null;

/** Why an Admin cannot sign in as this person in the Staff panel, or null when they can. */
function signInAsReason(person: Person, you: boolean): string | null {
  if (you) return "You are already you.";
  if (person.role === "admin") return "Nobody can sign in as an Admin.";
  if (person.status === "deactivated") return "They are deactivated. Reactivate them first.";
  if (!isStaffRole(person.role)) return "They no longer have Staff access.";
  return null;
}

const periodLabels: Record<Period, string> = {
  week: "This week",
  month: "Last 30 days",
  quarter: "Last 90 days",
};

/** The invalidation every change on this screen needs: the list and this page. */
function useRefresh() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ["admin", "team"] });
}

function StaffMemberScreen() {
  const { id } = Route.useParams();
  const { data } = useSuspenseQuery(teamMemberQuery(id));
  const { person, you } = data;
  const { user: me } = Route.useRouteContext();
  const signInAsBlocked = signInAsReason(person, you);
  const refresh = useRefresh();
  const [tab, setTab] = useState("overview");
  const [period, setPeriod] = useState<Period>("month");
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState<Confirm>(null);

  const hasAccess = isStaffRole(person.role);

  const resend = useMutation({
    mutationFn: () =>
      unwrap(api.admin.team.invites[":userId"].resend.$post({ param: { userId: person.id } })),
    onSuccess: async (result) => {
      await refresh();
      if (result.delivered) {
        toast.success(`Invitation sent again to ${person.email}`, {
          description: "The new link works for 7 days. Earlier links no longer work.",
        });
      } else toast.error("The invitation email did not go out. Try again in a minute.");
    },
    onError: (error) => toast.error(error.message),
  });

  const reset = useMutation({
    mutationFn: () =>
      unwrap(api.admin.team[":userId"]["password-reset"].$post({ param: { userId: person.id } })),
    onSuccess: async (result) => {
      await refresh();
      if (!result.delivered) toast.error("The email did not go out. Try again in a minute.");
      else if (result.sent === "invitation") {
        toast.success(`Invitation sent again to ${person.email}`, {
          description: "They had not chosen a password yet, so they got the invitation.",
        });
      } else {
        toast.success(`Password reset sent to ${person.email}`, {
          description: "The link works for one hour.",
        });
      }
    },
    onError: (error) => toast.error(error.message),
  });

  const sendConfirmation = useMutation({
    mutationFn: () => teamEmailActions.sendConfirmation(person.id),
    onSuccess: async (result) => {
      await refresh();
      toast.success(`Confirmation email sent to ${result.email}`, {
        description: `The link opens the ${result.app} and works for one hour.`,
      });
    },
    onError: (error) => toast.error(error.message),
  });
  const emailActions = person.emailVerified
    ? null
    : {
        // Sending is refused while they are deactivated: the link would not let them in.
        onSend: person.status === "deactivated" ? null : () => sendConfirmation.mutate(),
        sending: sendConfirmation.isPending,
        onConfirm: () => setConfirm("email"),
      };

  return (
    <>
      <Crumbs trail={[{ label: "Staff", to: "/staff" }, { label: person.name }]} />

      <Tabs value={tab} onValueChange={setTab} className="flex flex-col gap-y-3">
        <Container className="divide-y p-0">
          <SectionHeader
            level="h1"
            title={
              <span className="flex min-w-0 items-center gap-x-3">
                <Avatar fallback={initials(person.name)} shape="circle" />
                <span className="min-w-0 truncate">
                  {person.name}
                  {you ? (
                    <span className="txt-compact-small ml-2 font-normal text-ui-fg-muted">
                      (you)
                    </span>
                  ) : null}
                </span>
              </span>
            }
            description={person.title ? `${person.title}, ${person.email}` : person.email}
            actions={
              <>
                {/* Their whole account: workspaces they are a customer in, email, sign-in. */}
                <Button size="small" variant="secondary" asChild>
                  <Link to="/people/$id" params={{ id: person.id }}>
                    <User /> Open their profile
                  </Link>
                </Button>
                {signInAsBlocked === null ? (
                  <Button size="small" variant="secondary" onClick={() => setConfirm("sign-in-as")}>
                    <ArrowUpRightOnBox /> Sign in as
                  </Button>
                ) : null}
                <Button size="small" variant="secondary" onClick={() => setEditing(true)}>
                  <PencilSquare /> Edit
                </Button>
                <DropdownMenu>
                  <DropdownMenu.Trigger asChild>
                    <Button size="small" variant="secondary" aria-label="More actions">
                      <EllipsisHorizontal />
                    </Button>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Content align="end" className="max-w-[280px]">
                    {signInAsBlocked !== null && !you ? (
                      <>
                        <DropdownMenu.Item disabled>
                          <ArrowUpRightOnBox />
                          <MenuText label="Sign in as" reason={signInAsBlocked} />
                        </DropdownMenu.Item>
                        <DropdownMenu.Separator />
                      </>
                    ) : null}
                    {hasAccess && person.status === "invited" ? (
                      <DropdownMenu.Item onSelect={() => resend.mutate()}>
                        <Envelope /> Resend invitation
                      </DropdownMenu.Item>
                    ) : null}
                    {hasAccess && person.status === "active" ? (
                      <DropdownMenu.Item onSelect={() => reset.mutate()}>
                        <Key /> Send password reset
                      </DropdownMenu.Item>
                    ) : null}
                    {emailActions?.onSend ? (
                      <DropdownMenu.Item onSelect={emailActions.onSend}>
                        <Envelope /> Send a new confirmation email
                      </DropdownMenu.Item>
                    ) : null}
                    {emailActions ? (
                      <DropdownMenu.Item onSelect={emailActions.onConfirm}>
                        <CheckCircle /> Mark email as confirmed
                      </DropdownMenu.Item>
                    ) : null}
                    {person.status === "deactivated" ? (
                      <DropdownMenu.Item onSelect={() => setConfirm("reactivate")}>
                        <ArrowUturnLeft /> Reactivate
                      </DropdownMenu.Item>
                    ) : null}
                    {you ? (
                      <DropdownMenu.Label className="max-w-[240px] whitespace-normal">
                        Another Admin must deactivate you or remove your access.
                      </DropdownMenu.Label>
                    ) : (
                      <>
                        {hasAccess || person.status !== "deactivated" ? (
                          <DropdownMenu.Separator />
                        ) : null}
                        {hasAccess ? (
                          <DropdownMenu.Item tone="danger" onSelect={() => setConfirm("remove")}>
                            <MinusCircle /> Remove Staff access
                          </DropdownMenu.Item>
                        ) : null}
                        {person.status !== "deactivated" ? (
                          <DropdownMenu.Item
                            tone="danger"
                            onSelect={() => setConfirm("deactivate")}
                          >
                            <XCircle /> Deactivate
                          </DropdownMenu.Item>
                        ) : null}
                      </>
                    )}
                  </DropdownMenu.Content>
                </DropdownMenu>
              </>
            }
          />

          <div className="txt-compact-small flex flex-wrap items-center gap-x-3 gap-y-2 px-6 py-3 text-ui-fg-subtle">
            <RoleBadge role={person.role} />
            <StaffStatus status={person.status} />
            {person.emailVerified ? null : <EmailNotConfirmed />}
            <span>
              {person.status === "invited"
                ? "Has not signed in yet"
                : person.lastActiveAt
                  ? `Last active ${ago(person.lastActiveAt)}`
                  : "Not signed in anywhere right now"}
            </span>
          </div>

          <StatusNote
            person={person}
            onResend={() => resend.mutate()}
            resending={resend.isPending}
            onReactivate={() => setConfirm("reactivate")}
          />

          {emailActions ? <EmailNote {...emailActions} /> : null}

          <div className="px-6 py-3">
            <Tabs.List>
              <Tabs.Trigger value="overview">Overview</Tabs.Trigger>
              <Tabs.Trigger value="reviews">Reviews ({data.reviews.length})</Tabs.Trigger>
              <Tabs.Trigger value="corrections">
                Corrections ({data.corrections.quarter.total})
              </Tabs.Trigger>
              <Tabs.Trigger value="tickets">Tickets ({data.tickets.length})</Tabs.Trigger>
              <Tabs.Trigger value="writing">Articles and proposals</Tabs.Trigger>
              <Tabs.Trigger value="activity">Activity</Tabs.Trigger>
            </Tabs.List>
          </div>
        </Container>

        <Tabs.Content value="overview" className="flex flex-col gap-y-3">
          <Overview data={data} period={period} onPeriod={setPeriod} />
        </Tabs.Content>
        <Tabs.Content value="reviews">
          <Reviews reviews={data.reviews} />
        </Tabs.Content>
        <Tabs.Content value="corrections">
          <Corrections data={data} period={period} onPeriod={setPeriod} />
        </Tabs.Content>
        <Tabs.Content value="tickets">
          <Tickets tickets={data.tickets} />
        </Tabs.Content>
        <Tabs.Content value="writing" className="flex flex-col gap-y-3">
          <Writing data={data} />
        </Tabs.Content>
        <Tabs.Content value="activity">
          <Activity entries={data.activity} />
        </Tabs.Content>
      </Tabs>

      <Drawer open={editing} onOpenChange={setEditing}>
        <Drawer.Content>
          {editing ? (
            <EditForm person={person} you={you} onClose={() => setEditing(false)} />
          ) : null}
        </Drawer.Content>
      </Drawer>

      <DeactivatePrompt
        person={person}
        open={confirm === "deactivate"}
        onClose={() => setConfirm(null)}
      />
      <ReactivatePrompt
        person={person}
        open={confirm === "reactivate"}
        onClose={() => setConfirm(null)}
      />
      <RemoveAccessPrompt
        person={person}
        open={confirm === "remove"}
        onClose={() => setConfirm(null)}
      />
      <ConfirmEmailPrompt
        person={confirm === "email" ? person : null}
        confirm={() => teamEmailActions.confirm(person.id)}
        onDone={refresh}
        onClose={() => setConfirm(null)}
      />
      <SignInAsPrompt
        person={confirm === "sign-in-as" ? { userId: person.id, name: person.name } : null}
        surface="ops"
        adminName={me.name}
        onClose={() => setConfirm(null)}
      />
    </>
  );
}

/** An unconfirmed email keeps them from signing in. The two ways out, side by side. */
function EmailNote({
  onSend,
  sending,
  onConfirm,
}: {
  /** Null while they are deactivated: a link would not let them in. */
  onSend: (() => void) | null;
  sending: boolean;
  onConfirm: () => void;
}) {
  return (
    <div className="flex flex-col gap-3 bg-ui-bg-subtle px-6 py-3 lg:flex-row lg:items-center lg:justify-between">
      <p className="txt-small text-ui-fg-subtle">
        {onSend
          ? "Their email is not confirmed, so they cannot sign in until they open the link we email them. The link works for one hour."
          : "Their email is not confirmed either. Reactivate them before sending a new link."}
      </p>
      <div className="flex shrink-0 flex-wrap gap-2">
        {onSend ? (
          <Button size="small" variant="secondary" isLoading={sending} onClick={onSend}>
            <Envelope /> Send a new confirmation email
          </Button>
        ) : null}
        <Button size="small" variant="secondary" onClick={onConfirm}>
          <CheckCircle /> Mark email as confirmed
        </Button>
      </div>
    </div>
  );
}

/** What an invited or deactivated person's state means, with the one thing to do about it. */
function StatusNote({
  person,
  onResend,
  resending,
  onReactivate,
}: {
  person: Person;
  onResend: () => void;
  resending: boolean;
  onReactivate: () => void;
}) {
  if (person.status === "invited" && isStaffRole(person.role)) {
    return (
      <div className="flex flex-col gap-3 bg-ui-bg-subtle px-6 py-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="txt-small text-ui-fg-subtle">
          Invited {person.invitedAt ? ago(person.invitedAt) : ""}
          {person.invitedByName ? ` by ${person.invitedByName}` : ""}. They have not chosen a
          password yet. The link works for 7 days.
        </p>
        <Button
          size="small"
          variant="secondary"
          className="shrink-0"
          isLoading={resending}
          onClick={onResend}
        >
          <Envelope /> Resend invitation
        </Button>
      </div>
    );
  }
  if (person.status === "deactivated") {
    return (
      <div className="flex flex-col gap-3 bg-ui-bg-subtle px-6 py-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="txt-small text-ui-fg-subtle">
          Deactivated
          {person.banReason ? (
            <>
              : <span className="text-ui-fg-base">{person.banReason}</span>
            </>
          ) : null}
          . They cannot sign in. Their work stays on record.
        </p>
        <Button size="small" variant="secondary" className="shrink-0" onClick={onReactivate}>
          <ArrowUturnLeft /> Reactivate
        </Button>
      </div>
    );
  }
  if (!isStaffRole(person.role)) {
    return (
      <p className="txt-small bg-ui-bg-subtle px-6 py-3 text-ui-fg-subtle">
        Their Staff access was removed. Their work stays here. Give them a role from Edit to let
        them back in.
      </p>
    );
  }
  return null;
}

function PeriodTabs({ period, onPeriod }: { period: Period; onPeriod: (next: Period) => void }) {
  return (
    <Tabs value={period} onValueChange={(value) => onPeriod(value as Period)}>
      <Tabs.List>
        {(Object.keys(periodLabels) as Period[]).map((key) => (
          <Tabs.Trigger key={key} value={key}>
            {periodLabels[key]}
          </Tabs.Trigger>
        ))}
      </Tabs.List>
    </Tabs>
  );
}

function Overview({
  data,
  period,
  onPeriod,
}: {
  data: TeamMember;
  period: Period;
  onPeriod: (next: Period) => void;
}) {
  const { person } = data;
  return (
    <TwoColumn
      main={
        <Container className="divide-y overflow-hidden p-0">
          <SectionHeader
            title="What they produced"
            description="Counted from their reviews, tickets, articles, price proposals and lessons."
          />
          <div className="px-6 py-3">
            <PeriodTabs period={period} onPeriod={onPeriod} />
          </div>
          <NumbersGrid numbers={data.numbers[period]} />
        </Container>
      }
      side={
        <Container className="divide-y p-0">
          <SectionHeader title="Details" />
          <SectionRow label="Email">
            <a
              href={`mailto:${person.email}`}
              className="text-ui-fg-interactive hover:text-ui-fg-interactive-hover"
            >
              {person.email}
            </a>
          </SectionRow>
          <SectionRow label="Title">{person.title}</SectionRow>
          <SectionRow label="Phone">{person.phone}</SectionRow>
          <SectionRow label="On their desk">
            {data.openWork.reviews} {data.openWork.reviews === 1 ? "review" : "reviews"},{" "}
            {data.openWork.tickets} open {data.openWork.tickets === 1 ? "ticket" : "tickets"}
          </SectionRow>
          <SectionRow label="Invited">
            {person.invitedAt
              ? `${date(person.invitedAt)}${person.invitedByName ? ` by ${person.invitedByName}` : ""}`
              : null}
          </SectionRow>
          <SectionRow label="Account since">{date(person.createdAt)}</SectionRow>
          <SectionRow label="Notes">
            {person.notes ? <span className="whitespace-pre-line">{person.notes}</span> : null}
          </SectionRow>
        </Container>
      }
    />
  );
}

function Reviews({ reviews }: { reviews: TeamMember["reviews"] }) {
  return (
    <Container className="divide-y p-0">
      <SectionHeader
        title="Reviews"
        description="Reviews on their desk, and the ones they finished in the last 90 days. Each opens in the Staff panel."
      />
      {reviews.length === 0 ? (
        <EmptyState
          icon={<ListCheckbox />}
          title="No reviews yet"
          description="Takeoffs they claim and approve in the Staff panel show here."
        />
      ) : (
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Project</Table.HeaderCell>
              <Table.HeaderCell>Mode</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell className="text-right">Minutes</Table.HeaderCell>
              <Table.HeaderCell className="text-right">Corrections</Table.HeaderCell>
              <Table.HeaderCell>Approved</Table.HeaderCell>
              <Table.HeaderCell aria-label="Open in the Staff panel" />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {reviews.map((review) => (
              <Table.Row key={review.id}>
                <Table.Cell className="py-2.5">
                  <div className="txt-compact-small-plus whitespace-nowrap text-ui-fg-base">
                    {review.projectName}
                  </div>
                  <div className="txt-compact-xsmall whitespace-nowrap text-ui-fg-muted">
                    {review.workspaceName}
                  </div>
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  <ReviewMode mode={review.mode} status={review.status} />
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  <ReviewStatus status={review.status} />
                </Table.Cell>
                <Table.Cell className="tabular text-right">{minutes(review.minutes)}</Table.Cell>
                <Table.Cell className="tabular text-right">{review.corrections}</Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  {review.approvedAt ? dateTime(review.approvedAt) : "-"}
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap text-right">
                  <a
                    href={review.url}
                    target="_blank"
                    rel="noreferrer"
                    className="txt-compact-small-plus inline-flex items-center gap-x-1 text-ui-fg-interactive hover:text-ui-fg-interactive-hover"
                  >
                    Open <ArrowUpRightOnBox />
                  </a>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </Container>
  );
}

function Corrections({
  data,
  period,
  onPeriod,
}: {
  data: TeamMember;
  period: Period;
  onPeriod: (next: Period) => void;
}) {
  const mix = data.corrections[period];
  return (
    <Container className="divide-y p-0">
      <SectionHeader
        title="Corrections"
        description={`${mix.total} ${mix.total === 1 ? "correction" : "corrections"} while reviewing, ${periodLabels[period].toLowerCase()}. The reasons show where Reading goes wrong.`}
      />
      <div className="px-6 py-3">
        <PeriodTabs period={period} onPeriod={onPeriod} />
      </div>
      <div className="grid divide-ui-border-base max-lg:divide-y lg:grid-cols-2 lg:divide-x">
        <div>
          <p className="txt-compact-small-plus px-6 pt-4 text-ui-fg-base">By reason</p>
          <CountBars
            rows={mix.byReason.map((row) => ({ label: reasonLabel(row.reason), count: row.count }))}
            empty="No corrections in this period"
          />
        </div>
        <div>
          <p className="txt-compact-small-plus px-6 pt-4 text-ui-fg-base">By what they changed</p>
          <CountBars
            rows={mix.byCollection.map((row) => ({
              label: collectionLabel(row.collection),
              count: row.count,
            }))}
            empty="No corrections in this period"
          />
        </div>
      </div>
    </Container>
  );
}

function Tickets({ tickets }: { tickets: TeamMember["tickets"] }) {
  const navigate = useNavigate();
  return (
    <Container className="divide-y p-0">
      <SectionHeader
        title="Tickets"
        description="Tickets assigned to them or that they answered, newest activity first."
      />
      {tickets.length === 0 ? (
        <EmptyState
          icon={<Lifebuoy />}
          title="No tickets yet"
          description="Tickets they are assigned or reply to show here."
        />
      ) : (
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Ticket</Table.HeaderCell>
              <Table.HeaderCell>From</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Their part</Table.HeaderCell>
              <Table.HeaderCell>Last activity</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {tickets.map((ticket) => (
              <Table.Row
                key={ticket.id}
                data-clickable="true"
                onClick={() => navigate({ to: "/support/tickets/$id", params: { id: ticket.id } })}
              >
                <Table.Cell className="py-2.5">
                  <div className="txt-compact-small-plus max-w-[320px] truncate text-ui-fg-base">
                    {ticket.title}
                  </div>
                  <div className="txt-compact-xsmall text-ui-fg-muted">#{shortId(ticket.id)}</div>
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  {ticket.workspaceName ?? ticket.contactName ?? (
                    <span className="text-ui-fg-muted">Staff</span>
                  )}
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  <TicketStatusBadge status={ticket.status} />
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  {[
                    ticket.assignedToThem ? "Assigned" : null,
                    ticket.replies > 0
                      ? `${ticket.replies} ${ticket.replies === 1 ? "reply" : "replies"}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(", ") || "-"}
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">{ago(ticket.lastActivityAt)}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </Container>
  );
}

const proposalKinds: Record<string, string> = {
  new_item: "New item",
  price_update: "New price",
  edit: "Edit",
  market_adjustment: "Market move",
  import: "Import",
};

const decisionLooks: Record<string, { label: string; tone: Tone }> = {
  pending: { label: "Waiting", tone: "orange" },
  approved: { label: "Approved", tone: "green" },
  rejected: { label: "Rejected", tone: "red" },
  withdrawn: { label: "Withdrawn", tone: "grey" },
  draft: { label: "Draft", tone: "grey" },
  published: { label: "Published", tone: "green" },
  retired: { label: "Retired", tone: "grey" },
};

function Decision({ status }: { status: string }) {
  const look = decisionLooks[status] ?? { label: status, tone: "grey" as const };
  return <StatusBadge color={look.tone}>{look.label}</StatusBadge>;
}

const passLabels: Record<string, string> = {
  any: "Every pass",
  notes: "Notes",
  floorPlan: "Floor plans",
  framing: "Framing",
  roof: "Roof",
  heights: "Heights",
};

function Writing({ data }: { data: TeamMember }) {
  const navigate = useNavigate();
  return (
    <>
      <Container className="divide-y p-0">
        <SectionHeader title="Help articles" description="Articles they started, newest first." />
        {data.articles.length === 0 ? (
          <EmptyState className="py-10" icon={<BookOpen />} title="No help articles yet" />
        ) : (
          <ul className="divide-y divide-ui-border-base">
            {data.articles.map((article) => (
              <li key={article.id}>
                <Link
                  to="/help/articles/$id"
                  params={{ id: article.id }}
                  className="flex items-center justify-between gap-x-4 px-6 py-3 hover:bg-ui-bg-base-hover"
                >
                  <span className="min-w-0">
                    <span className="txt-compact-small-plus block truncate text-ui-fg-base">
                      {article.title}
                    </span>
                    <span className="txt-compact-xsmall block text-ui-fg-muted">
                      Updated {ago(article.updatedAt)}, {article.views}{" "}
                      {article.views === 1 ? "view" : "views"}
                    </span>
                  </span>
                  <Decision status={article.status} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Container>

      <Container className="divide-y p-0">
        <SectionHeader
          title="Price proposals"
          description="Changes to the reference price book they put forward, and what became of them."
        />
        {data.proposals.length === 0 ? (
          <EmptyState className="py-10" title="No price proposals yet" />
        ) : (
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Item</Table.HeaderCell>
                <Table.HeaderCell>Change</Table.HeaderCell>
                <Table.HeaderCell className="text-right">Price</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Proposed</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {data.proposals.map((proposal) => (
                <Table.Row
                  key={proposal.id}
                  data-clickable="true"
                  onClick={() => navigate({ to: "/reference-prices" })}
                >
                  <Table.Cell className="py-2.5">
                    <div className="txt-compact-small-plus max-w-[280px] truncate text-ui-fg-base">
                      {proposal.description}
                    </div>
                    <div className="txt-compact-xsmall text-ui-fg-muted">
                      {proposal.code}, {proposal.region}
                    </div>
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    {proposalKinds[proposal.kind] ?? proposal.kind}
                  </Table.Cell>
                  <Table.Cell className="tabular whitespace-nowrap text-right">
                    {proposal.previousPriceCents !== null ? (
                      <span className="text-ui-fg-muted">
                        {money(proposal.previousPriceCents)} to{" "}
                      </span>
                    ) : null}
                    <span className="text-ui-fg-base">{money(proposal.priceCents)}</span>
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    <Decision status={proposal.status} />
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">{date(proposal.createdAt)}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Container>

      <Container className="divide-y p-0">
        <SectionHeader
          title="Reading lessons"
          description="Rules they wrote so Reading does not make the same mistake twice."
        />
        {data.lessons.length === 0 ? (
          <EmptyState className="py-10" title="No reading lessons yet" />
        ) : (
          <ul className="divide-y divide-ui-border-base">
            {data.lessons.map((lesson) => (
              <li key={lesson.id} className="flex items-start justify-between gap-x-4 px-6 py-3">
                <span className="min-w-0">
                  <span className="txt-small block text-pretty text-ui-fg-base">{lesson.text}</span>
                  <span className="txt-compact-xsmall block text-ui-fg-muted">
                    {passLabels[lesson.pass] ?? lesson.pass}, written {date(lesson.createdAt)}
                  </span>
                </span>
                <Decision status={lesson.status} />
              </li>
            ))}
          </ul>
        )}
      </Container>
    </>
  );
}

/** One line of JSON, or nothing when there is nothing to say. */
function compactJson(data: Record<string, unknown> | null): string {
  if (!data || Object.keys(data).length === 0) return "";
  return JSON.stringify(data);
}

function Activity({ entries }: { entries: TeamMember["activity"] }) {
  return (
    <Container className="divide-y p-0">
      <SectionHeader
        title="Activity"
        description="What they changed across the platform, from the audit log. The last 100 entries."
      />
      {entries.length === 0 ? (
        <EmptyState
          icon={<ScrollText />}
          title="Nothing recorded yet"
          description="Changes they make to billing, workspaces, Staff or settings land here."
        />
      ) : (
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Time</Table.HeaderCell>
              <Table.HeaderCell>Action</Table.HeaderCell>
              <Table.HeaderCell>About</Table.HeaderCell>
              <Table.HeaderCell>Details</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {entries.map((entry) => {
              const json = compactJson(entry.data);
              return (
                <Table.Row key={entry.id}>
                  <Table.Cell className="whitespace-nowrap">{dateTime(entry.createdAt)}</Table.Cell>
                  <Table.Cell className="min-w-[240px]">
                    <span className="txt-compact-small text-ui-fg-base">
                      {auditSentence(entry.action, entry.data)}
                    </span>
                    <div className="txt-compact-xsmall font-mono text-ui-fg-muted">
                      {entry.action}
                    </div>
                    {entry.impersonatorId ? (
                      <div className="txt-compact-xsmall text-ui-fg-muted">while impersonated</div>
                    ) : null}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    {entry.workspaceName ??
                      (entry.targetType ? auditTargetLabel(entry.targetType) : "-")}
                    {entry.targetId ? (
                      <div className="txt-compact-xsmall font-mono text-ui-fg-muted">
                        {entry.targetId}
                      </div>
                    ) : null}
                  </Table.Cell>
                  <Table.Cell>
                    {json ? (
                      <code
                        className="txt-compact-xsmall block max-w-[420px] truncate font-mono text-ui-fg-subtle"
                        title={json}
                      >
                        {json}
                      </code>
                    ) : (
                      "-"
                    )}
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      )}
    </Container>
  );
}

/* ---- Changes ------------------------------------------------------------ */

/** Name, role, title, phone and notes. Sends only what changed. */
function EditForm({ person, you, onClose }: { person: Person; you: boolean; onClose: () => void }) {
  const refresh = useRefresh();
  const [role, setRole] = useState<string>(isStaffRole(person.role) ? person.role : "");
  const chosen = roleChoices.find((choice) => choice.value === role);

  const save = useMutation({
    mutationFn: (form: FormData) => {
      const text = (name: string) => String(form.get(name) ?? "").trim();
      const json: {
        name?: string;
        role?: StaffRoleValue;
        title?: string | null;
        phone?: string | null;
        notes?: string | null;
      } = {};
      if (text("name") && text("name") !== person.name) json.name = text("name");
      if (isStaffRole(role) && role !== person.role) json.role = role;
      if (text("title") !== (person.title ?? "")) json.title = text("title") || null;
      if (text("phone") !== (person.phone ?? "")) json.phone = text("phone") || null;
      if (text("notes") !== (person.notes ?? "")) json.notes = text("notes") || null;
      return unwrap(api.admin.team[":userId"].$patch({ param: { userId: person.id }, json }));
    },
    onSuccess: async ({ person: saved }) => {
      await refresh();
      toast.success(`${saved.name} saved`);
      onClose();
    },
    onError: (error) => toast.error(error.message),
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    save.mutate(new FormData(event.currentTarget));
  }

  return (
    <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
      <Drawer.Header>
        <Drawer.Title>Edit {person.name}</Drawer.Title>
        <Drawer.Description>
          A new role takes effect on their next click. Notes are for Admins only.
        </Drawer.Description>
      </Drawer.Header>

      <Drawer.Body className="flex flex-col gap-y-5">
        <Field label="Name">
          {(control) => (
            <Input {...control} name="name" required maxLength={120} defaultValue={person.name} />
          )}
        </Field>
        <Field
          label="Role"
          hint={you ? "You cannot change your own role. Ask another Admin." : chosen?.hint}
        >
          {(control) => (
            <Select value={role} onValueChange={setRole} disabled={you}>
              <Select.Trigger id={control.id} aria-describedby={control["aria-describedby"]}>
                <Select.Value placeholder="No Staff access" />
              </Select.Trigger>
              <Select.Content>
                {roleChoices.map((choice) => (
                  <Select.Item key={choice.value} value={choice.value}>
                    {choice.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select>
          )}
        </Field>
        <Field label="Title" optional>
          {(control) => (
            <Input
              {...control}
              name="title"
              maxLength={120}
              placeholder="Senior estimator"
              defaultValue={person.title ?? ""}
            />
          )}
        </Field>
        <Field label="Phone" optional>
          {(control) => (
            <Input
              {...control}
              name="phone"
              type="tel"
              maxLength={40}
              defaultValue={person.phone ?? ""}
            />
          )}
        </Field>
        <Field
          label="Notes"
          optional
          hint="Only Admins see these notes. The Staff member never does."
        >
          {(control) => (
            <Textarea
              {...control}
              name="notes"
              rows={4}
              maxLength={4000}
              defaultValue={person.notes ?? ""}
            />
          )}
        </Field>
      </Drawer.Body>

      <Drawer.Footer>
        <Button type="button" variant="secondary" size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" size="small" isLoading={save.isPending}>
          Save
        </Button>
      </Drawer.Footer>
    </form>
  );
}

function DeactivatePrompt({
  person,
  open,
  onClose,
}: {
  person: Person;
  open: boolean;
  onClose: () => void;
}) {
  const refresh = useRefresh();
  const [reason, setReason] = useState("");
  const deactivate = useMutation({
    mutationFn: () =>
      unwrap(
        api.admin.team[":userId"].deactivate.$post({
          param: { userId: person.id },
          json: { reason: reason.trim() },
        }),
      ),
    onSuccess: async () => {
      await refresh();
      toast.success(`${person.name} is deactivated`, {
        description: "They were signed out everywhere.",
      });
      setReason("");
      onClose();
    },
    onError: (error) => toast.error(error.message),
  });

  return (
    <Prompt open={open} onOpenChange={(next) => !next && onClose()}>
      <Prompt.Content>
        <Prompt.Header>
          <Prompt.Title>Deactivate {person.name}?</Prompt.Title>
          <Prompt.Description>
            They are signed out everywhere and cannot sign in until you reactivate them. Their work
            stays on record.
          </Prompt.Description>
        </Prompt.Header>
        <div className="px-6 pt-4">
          <Field label="Reason" hint="Kept with their account. For example: left the company.">
            {(control) => (
              <Input
                {...control}
                value={reason}
                maxLength={300}
                onChange={(event) => setReason(event.target.value)}
              />
            )}
          </Field>
        </div>
        <Prompt.Footer>
          <Prompt.Cancel>Keep them</Prompt.Cancel>
          <Button
            size="small"
            variant="danger"
            disabled={reason.trim().length < 3}
            isLoading={deactivate.isPending}
            onClick={() => deactivate.mutate()}
          >
            Deactivate
          </Button>
        </Prompt.Footer>
      </Prompt.Content>
    </Prompt>
  );
}

function ReactivatePrompt({
  person,
  open,
  onClose,
}: {
  person: Person;
  open: boolean;
  onClose: () => void;
}) {
  const refresh = useRefresh();
  const reactivate = useMutation({
    mutationFn: () =>
      unwrap(api.admin.team[":userId"].reactivate.$post({ param: { userId: person.id } })),
    onSuccess: async () => {
      await refresh();
      toast.success(`${person.name} can sign in again`);
      onClose();
    },
    onError: (error) => toast.error(error.message),
  });

  return (
    <Prompt open={open} onOpenChange={(next) => !next && onClose()}>
      <Prompt.Content>
        <Prompt.Header>
          <Prompt.Title>Reactivate {person.name}?</Prompt.Title>
          <Prompt.Description>
            They can sign in again with the password they had, with the role they had.
          </Prompt.Description>
        </Prompt.Header>
        <Prompt.Footer>
          <Prompt.Cancel>Cancel</Prompt.Cancel>
          <Button size="small" isLoading={reactivate.isPending} onClick={() => reactivate.mutate()}>
            Reactivate
          </Button>
        </Prompt.Footer>
      </Prompt.Content>
    </Prompt>
  );
}

function RemoveAccessPrompt({
  person,
  open,
  onClose,
}: {
  person: Person;
  open: boolean;
  onClose: () => void;
}) {
  const refresh = useRefresh();
  const remove = useMutation({
    mutationFn: () =>
      unwrap(api.admin.team[":userId"]["remove-access"].$post({ param: { userId: person.id } })),
    onSuccess: async () => {
      await refresh();
      toast.success(`${person.name} no longer has Staff access`);
      onClose();
    },
    onError: (error) => toast.error(error.message),
  });

  return (
    <Prompt open={open} onOpenChange={(next) => !next && onClose()}>
      <Prompt.Content>
        <Prompt.Header>
          <Prompt.Title>Remove Staff access for {person.name}?</Prompt.Title>
          <Prompt.Description>
            Their account becomes an ordinary LumberSet account. They stay under Former staff, with
            everything they did.
          </Prompt.Description>
        </Prompt.Header>
        <Prompt.Footer>
          <Prompt.Cancel>Keep access</Prompt.Cancel>
          <Button
            size="small"
            variant="danger"
            isLoading={remove.isPending}
            onClick={() => remove.mutate()}
          >
            Remove access
          </Button>
        </Prompt.Footer>
      </Prompt.Content>
    </Prompt>
  );
}
